/**
 * Card surface response.
 *
 * Pointer devices tilt the card toward the cursor. Touch devices with a motion
 * sensor tilt it against a slowly-following baseline, so the current viewing
 * angle always reads as neutral and a movement settles back to rest instead of
 * pinning the card at an extreme. Reduced motion disables both.
 *
 * Written for this project; the technique was calibrated against publicly
 * readable collectible-card work, not copied from it.
 */

const MAX_TILT = 9;
const SETTLE_MS = 1600;

type OrientationConstructor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function apply(card: HTMLElement, rotX: number, rotY: number): void {
  const lightX = 50 + (rotY / MAX_TILT) * 42;
  card.style.setProperty('--card-rx', `${rotX.toFixed(2)}deg`);
  card.style.setProperty('--card-ry', `${rotY.toFixed(2)}deg`);
  card.style.setProperty('--card-lx', `${lightX.toFixed(1)}%`);
  card.style.setProperty('--card-ly', `${(50 - (rotX / MAX_TILT) * 42).toFixed(1)}%`);
  // Unitless companion so the foil can drive hue and grating angle from it.
  card.style.setProperty('--card-light', lightX.toFixed(1));
}

function rest(card: HTMLElement): void {
  card.style.removeProperty('--card-rx');
  card.style.removeProperty('--card-ry');
  card.style.removeProperty('--card-lx');
  card.style.removeProperty('--card-ly');
  card.style.removeProperty('--card-light');
}

export function attachCardMotion(card: HTMLElement): () => void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};

  const controller = new AbortController();
  const { signal } = controller;
  const coarse = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  if (!coarse) {
    card.addEventListener(
      'pointermove',
      (event: PointerEvent) => {
        if (event.pointerType === 'touch') return;
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        apply(card, clamp(-y, -0.5, 0.5) * 2 * MAX_TILT, clamp(x, -0.5, 0.5) * 2 * MAX_TILT);
      },
      { signal },
    );
    card.addEventListener('pointerleave', () => rest(card), { signal });
    return () => {
      controller.abort();
      rest(card);
    };
  }

  if (!('DeviceOrientationEvent' in window)) return () => controller.abort();

  const listen = () => {
    let baseBeta: number | null = null;
    let baseGamma: number | null = null;
    let last = performance.now();

    window.addEventListener(
      'deviceorientation',
      (event: DeviceOrientationEvent) => {
        if (event.beta === null || event.gamma === null) return;
        const now = performance.now();
        const elapsed = Math.min(now - last, 120);
        last = now;

        if (baseBeta === null || baseGamma === null) {
          baseBeta = event.beta;
          baseGamma = event.gamma;
          return;
        }

        const follow = 1 - Math.exp((-elapsed * Math.log(100)) / SETTLE_MS);
        baseBeta += (event.beta - baseBeta) * follow;
        baseGamma += (event.gamma - baseGamma) * follow;

        apply(
          card,
          clamp((event.beta - baseBeta) / 16, -1, 1) * -MAX_TILT,
          clamp((event.gamma - baseGamma) / 16, -1, 1) * MAX_TILT,
        );
      },
      { signal },
    );
  };

  const orientation = DeviceOrientationEvent as OrientationConstructor;
  if (typeof orientation.requestPermission === 'function') {
    // iOS only grants motion access from a user gesture.
    card.addEventListener(
      'click',
      () => {
        orientation.requestPermission?.().then((state) => {
          if (state === 'granted') listen();
        }).catch(() => {});
      },
      { once: true, signal },
    );
  } else {
    listen();
  }

  return () => {
    controller.abort();
    rest(card);
  };
}
